// Client for the kmapi vault + login-key endpoints. All crypto (KDF, wrap/unwrap)
// runs locally via keyvault.js; this module only moves ciphertext + a one-way
// login key over the wire. `auth` is a full Authorization header value
// ("Bearer <token>" after login, or "Basic <b64>" during migration).
import * as kv from './keyvault.js';

const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

async function req(method, path, { auth, body } = {}) {
  const headers = {};
  if (auth) headers.Authorization = auth;
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const r = await fetch(path, { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined });
  let data = null; try { data = await r.json(); } catch { /* empty */ }
  return { ok: r.ok, status: r.status, data: data || {} };
}

// --- login-key auth (server never sees the password) ---

// Returns { token } on success; null if the password is wrong OR no vault exists
// (caller then tries the migration path). Throws {rate:true} when rate-limited.
export async function loginWithPassword(email, password) {
  const begin = await req('POST', '/auth/v1/login/begin', { body: { email } });
  if (!begin.ok || !begin.data.salt) throw new Error('login unavailable');
  const loginKey = await kv.deriveLoginKey(password, unb64(begin.data.salt));
  const fin = await req('POST', '/auth/v1/login/finish', { body: { email, loginKey } });
  if (fin.status === 429) throw Object.assign(new Error('Too many attempts, try again later'), { rate: true });
  if (!fin.ok) return null;
  return fin.data.token;
}

export async function logout(token) { if (token) await req('POST', '/auth/v1/logout', { auth: 'Bearer ' + token }).catch(() => {}); }

// --- vault storage (auth = Bearer token, or Basic during migration) ---

export async function getVault(auth) {
  const r = await req('GET', '/auth/v1/vault', { auth });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error('vault fetch failed: ' + r.status);
  return r.data;
}
export async function vaultStatus(auth) {
  const r = await req('GET', '/auth/v1/vault/status', { auth });
  return r.ok ? r.data : { exists: false };
}
export async function putVault(auth, vault) {
  const r = await req('PUT', '/auth/v1/vault', { auth, body: vault });
  if (!r.ok) throw new Error('vault save failed: ' + r.status);
  return r.data;
}
// Set the Stalwart mail credential (random) for the account. auth = Basic (migration) or Bearer.
export async function setMailCred(auth, mailCred) {
  const r = await req('POST', '/auth/v1/mailcred', { auth, body: { mailCred } });
  if (!r.ok) throw new Error('mail credential setup failed: ' + r.status);
  return r.data;
}

// Generate a fresh random mail credential (high entropy, URL-safe).
export function newMailCred() {
  const b = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...b)).replace(/[+/=]/g, (c) => ({ '+': 'A', '/': 'B', '=': '' }[c]));
}
